// Offline drafts + pending submission queue (localStorage) and offline photos (IndexedDB)
import { createStore, get, set, del } from "idb-keyval";

const DRAFT_PREFIX = "ir_draft_";
const QUEUE_KEY = "ir_pending_queue";
const photoStore = createStore("ir-photos", "photos");

export const draftKey = (id, type) => DRAFT_PREFIX + (id ? `edit_${id}` : `new_${type || "pv_liberation"}`);

export function saveDraft(key, data) {
  try { localStorage.setItem(key, JSON.stringify({ data, savedAt: Date.now() })); } catch (e) { /* quota */ }
}
export function loadDraft(key) {
  try { const r = localStorage.getItem(key); return r ? JSON.parse(r) : null; } catch (e) { return null; }
}
export function clearDraft(key) {
  try { localStorage.removeItem(key); } catch (e) { /* noop */ }
}

export function getQueue() {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); } catch (e) { return []; }
}
function setQueue(q) {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); } catch (e) { /* noop */ }
}
export function enqueue(item) {
  const q = getQueue();
  const qid = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  q.push({ ...item, qid, savedAt: Date.now() });
  setQueue(q);
  return qid;
}
export const getQueueItem = (qid) => getQueue().find((i) => i.qid === qid);
export function updateQueueItem(qid, payload) {
  setQueue(getQueue().map((i) => (i.qid === qid ? { ...i, payload, savedAt: Date.now() } : i)));
}
export function removeQueueItem(qid) {
  setQueue(getQueue().filter((i) => i.qid !== qid));
}

// Bons created offline in a folder ("none" = Sans dossier)
export function getPendingBons(folder) {
  return getQueue().filter((i) => i.method === "post" && i.url === "/interventions"
    && (folder === undefined || (i.payload?.folder_id || "none") === folder));
}
export function getPendingFolders() {
  return getQueue().filter((i) => i.url === "/folders").map((i) => ({ id: i.temp_id, name: i.payload.name, pending: true, qid: i.qid }));
}
export function enqueueFolder(name) {
  const temp_id = `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  enqueue({ method: "post", url: "/folders", payload: { name }, temp_id });
  return temp_id;
}

export function compressImage(file, max = 1600, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const r = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * r);
      canvas.height = Math.round(img.height * r);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("compress"))), "image/jpeg", quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("image")); };
    img.src = url;
  });
}

export async function saveLocalPhoto(blob) {
  const id = `lp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  await set(id, blob, photoStore);
  return id;
}
export const getLocalPhoto = (id) => get(id, photoStore);
export const localPhotoIds = (photos) => (photos || []).filter((p) => p.local_id && !p.storage_path).map((p) => p.local_id);
export async function deleteLocalPhotos(ids) {
  for (const id of ids) await del(id, photoStore).catch(() => {});
}

// Uploads photos kept on the device; blobs are deleted by the caller once the bon is saved
export async function uploadLocalPhotos(api, photos) {
  const out = [];
  for (const ph of photos || []) {
    if (!ph.local_id || ph.storage_path) { out.push(ph); continue; }
    const blob = await get(ph.local_id, photoStore);
    if (!blob) continue;
    const fd = new FormData();
    fd.append("file", blob, "photo.jpg");
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    const { local_id, ...rest } = ph;
    out.push({ ...rest, storage_path: data.storage_path });
  }
  return out;
}

let flushing = null;

// Single-flight: concurrent calls share the same run (avoids duplicate bons / emails)
export function flushQueue(api) {
  if (!flushing) flushing = runFlush(api).finally(() => { flushing = null; });
  return flushing;
}

async function runFlush(api) {
  if (typeof navigator !== "undefined" && !navigator.onLine) return { sent: 0, remaining: getQueue().length, failed: 0 };
  const q = getQueue();
  if (!q.length) return { sent: 0, remaining: 0, failed: 0 };
  const remaining = [];
  const idMap = {};
  let sent = 0;
  let failed = 0;
  for (const item of q) {
    try {
      if (item.payload?.folder_id && idMap[item.payload.folder_id]) {
        item.payload = { ...item.payload, folder_id: idMap[item.payload.folder_id] };
      }
      if (item.url === "/folders") {
        try {
          const { data } = await api.post("/folders", item.payload);
          idMap[item.temp_id] = data.id;
        } catch (e) {
          if (!e.response || e.response.status === 401) throw e;
          // name already exists: reuse that folder
          const { data } = await api.get("/folders");
          const found = data.folders.find((f) => f.name === item.payload.name);
          if (!found) throw e;
          idMap[item.temp_id] = found.id;
        }
        sent++;
        continue;
      }
      if (item.payload?.folder_id && String(item.payload.folder_id).startsWith("tmp_")) {
        item.payload = { ...item.payload, folder_id: null };
      }
      const ids = localPhotoIds(item.payload?.photos);
      if (ids.length) item.payload = { ...item.payload, photos: await uploadLocalPhotos(api, item.payload.photos) };
      if (item.method === "put") await api.put(item.url, item.payload);
      else await api.post(item.url, item.payload);
      await deleteLocalPhotos(ids);
      sent++;
    } catch (e) {
      // keep on network errors and expired session (retry after login); other refusals are reported
      if (!e.response || e.response.status === 401) remaining.push(item);
      else failed++;
    }
  }
  // keep items queued meanwhile (e.g. saved during the sync)
  const added = getQueue().filter((i) => !q.some((o) => o.qid === i.qid)).map((i) => (
    i.payload?.folder_id && idMap[i.payload.folder_id] ? { ...i, payload: { ...i.payload, folder_id: idMap[i.payload.folder_id] } } : i));
  const kept = remaining.map((i) => (
    i.payload?.folder_id && idMap[i.payload.folder_id] ? { ...i, payload: { ...i.payload, folder_id: idMap[i.payload.folder_id] } } : i));
  setQueue([...kept, ...added]);
  if (sent > 0 && typeof window !== "undefined") window.dispatchEvent(new Event("ir-synced"));
  return { sent, remaining: remaining.length + added.length, failed };
}
